import { XIcon } from 'lucide-react';
import * as React from 'react';

import { Input } from './components/ui/input.js';
import { clearNativeInput } from './lib/clear-native-input.js';
import { splitInputLayoutClasses } from './lib/split-input-layout-classes.js';
import { TOUCH_LAYOUT_CLASSES } from './lib/touch-layout-classes.js';
import { cn } from './lib/utils.js';

const CLEARABLE_TYPES = new Set(['text', 'email', 'tel', 'url', 'search']);
const NO_SPIN_BUTTON_CLASSES =
  '[appearance:textfield] [&::-webkit-inner-spin-button]:appearance-none [&::-webkit-outer-spin-button]:appearance-none';

type DimeInputProps = Readonly<
  Omit<React.ComponentProps<typeof Input>, 'size'> & {
    /** Drops a number input's built-in up/down arrows, for a field that sits between its own − and + buttons. */
    hideSpinButtons?: boolean;
    /** Shows an X that empties the field while it has a value; off for number, date, password and file inputs. */
    showClear?: boolean;
    /** `touch` is a 44px target for a picker's tablet or phone. */
    size?: 'default' | 'touch';
    /** Sits inside the control's right edge, such as a completion check; pass `null` while it is hidden so toggling it keeps focus; replaces the clear X. */
    trailing?: React.ReactNode;
  }
>;

export function DimeInput({
  showClear = true,
  size = 'default',
  className: callerClassName,
  hideSpinButtons = false,
  ref,
  type,
  trailing,
  ...props
}: DimeInputProps) {
  const className = cn(hideSpinButtons && NO_SPIN_BUTTON_CLASSES, callerClassName);
  const sizedClassName = cn(size === 'touch' && TOUCH_LAYOUT_CLASSES.control, className);

  if (trailing !== undefined) {
    return (
      <div className="relative">
        <Input
          ref={ref}
          type={type}
          {...props}
          className={cn(
            size === 'touch' && TOUCH_LAYOUT_CLASSES.control,
            trailing ? 'pr-8' : undefined,
            className,
          )}
        />
        <span className="absolute inset-y-0 right-2 flex items-center">{trailing}</span>
      </div>
    );
  }

  if (!showClear || !CLEARABLE_TYPES.has(type ?? 'text')) {
    return <Input ref={ref} type={type} className={sizedClassName} {...props} />;
  }

  const { layout, field } = splitInputLayoutClasses(sizedClassName);

  return (
    <div
      data-slot="input-clearable"
      className={cn('group/clearable relative w-full min-w-0', layout)}
    >
      <Input
        ref={ref}
        type={type}
        className={cn(
          'peer not-placeholder-shown:group-hover/clearable:pr-8 not-placeholder-shown:focus:pr-8 [&::-webkit-search-cancel-button]:appearance-none',
          field,
        )}
        {...props}
        // WHY: `:placeholder-shown` is how the X knows the field is empty, and it needs a placeholder to match.
        placeholder={props.placeholder ?? ' '}
      />
      <button
        type="button"
        tabIndex={-1}
        aria-hidden
        className="invisible absolute top-1/2 right-1.5 flex size-5 -translate-y-1/2 items-center justify-center rounded-sm text-muted-foreground group-hover/clearable:visible peer-focus:visible hover:bg-muted hover:text-foreground peer-placeholder-shown:hidden peer-disabled:hidden peer-read-only:hidden"
        onMouseDown={(event) => event.preventDefault()}
        // The X always renders straight after its input.
        onClick={(event) =>
          clearNativeInput(event.currentTarget.previousElementSibling as HTMLInputElement)
        }
      >
        <XIcon className="size-3.5" />
      </button>
    </div>
  );
}
